import { Injectable } from '@angular/core';
import { MakDevice, MakMp3 } from '@mommy/models/Eline.model';
import { MakService } from '@mommy/services/eline/mak-service';
import { StorageService } from '@mommy/services/storage.service';
import { UtilService } from '@mommy/services/util.service';
import {
  Action,
  createSelector,
  NgxsAfterBootstrap,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { AppSettings } from 'app/app.settings';
import * as _ from 'lodash';
import {
  BindMak,
  CheckMakStatus,
  GetMakDevices,
  GetMakMp3s,
  InitLocalCacheMakDevices,
  LoadCacheMakDevices,
  ProcessMakDevice,
  RefreshMakDevices,
} from './mak-device.actions';

export interface MakDeviceStateModel {
  loading: boolean;
  makDevices: MakDevice[];
  makMp3Map: { [mak_device_id: number]: MakMp3[] }; // key: mak_device_id, value: mp3 list
  checkStatusResult: { team_id?: number; customer_id?: number } | null;
  bindResult: { success: boolean; message: string } | null;
  hasCache: boolean;
  lastProcessedMacAddress: string | null;
}

const defaultMakDeviceState = (): MakDeviceStateModel => {
  return {
    loading: false,
    makDevices: [],
    makMp3Map: {},
    checkStatusResult: null,
    bindResult: null,
    hasCache: false,
    lastProcessedMacAddress: null,
  };
};

@State<MakDeviceStateModel>({
  name: 'MakDeviceState',
  defaults: defaultMakDeviceState(),
})
@Injectable()
export class MakDeviceState implements NgxsAfterBootstrap {
  constructor(
    private storage: StorageService,
    private makService: MakService,
    private util: UtilService
  ) {}

  async ngxsAfterBootstrap(ctx: StateContext<MakDeviceStateModel | null>) {
    console.log('[MakDeviceState] ngxsAfterBootstrap');
  }

  // 取得所有 MAK 裝置
  @Selector()
  static makDevices(state: MakDeviceStateModel) {
    return state.makDevices;
  }

  // 查詢 MAK 綁定狀態結果
  @Selector()
  static checkStatusResult(state: MakDeviceStateModel) {
    return state.checkStatusResult;
  }

  // 綁定結果
  @Selector()
  static bindResult(state: MakDeviceStateModel) {
    return state.bindResult;
  }

  // 是否有快取
  @Selector()
  static hasCache(state: MakDeviceStateModel) {
    return state.hasCache;
  }

  // 取得最後處理的 MAC 位址
  @Selector()
  static lastProcessedMacAddress(state: MakDeviceStateModel) {
    return state.lastProcessedMacAddress;
  }

  // 建立 dynamic selector, 傳入 mak_device_id, 回傳對應的 MAK 裝置
  static makDeviceById(mak_device_id: number) {
    return createSelector([MakDeviceState], (state: MakDeviceStateModel) => {
      return _.find(state.makDevices, { mak_device_id });
    });
  }

  // 建立 dynamic selector, 傳入 mak_device_id, 回傳對應的音檔清單
  static makMp3sByDeviceId(mak_device_id: number) {
    return createSelector([MakDeviceState], (state: MakDeviceStateModel) => {
      return state.makMp3Map[mak_device_id] || [];
    });
  }

  // 依照 team_id 篩選 MAK 裝置
  static makDevicesByTeamId(team_id: number) {
    return createSelector([MakDeviceState], (state: MakDeviceStateModel) => {
      return _.filter(state.makDevices, { team_id });
    });
  }

  // 依照 customer_id 篩選 MAK 裝置
  static makDevicesByCustomerId(customer_id: number) {
    return createSelector([MakDeviceState], (state: MakDeviceStateModel) => {
      return _.filter(state.makDevices, { customer_id });
    });
  }

  // 依照 mac_address 查詢 MAK 裝置
  static makDeviceByMacAddress(mac_address: string) {
    return createSelector([MakDeviceState], (state: MakDeviceStateModel) => {
      return _.find(state.makDevices, { mac_address });
    });
  }

  @Action(InitLocalCacheMakDevices)
  async initLocalCacheMakDevices(ctx: StateContext<MakDeviceStateModel>) {
    console.log('[Action] InitLocalCacheMakDevices');

    // 策略：
    // 1. 先載入 local cache，如果沒有就抓 server
    // 2. 載入完 cache 再抓 server 更新的資料
    try {
      await ctx.dispatch(new LoadCacheMakDevices()).toPromise();
      console.log('load local cache mak devices success');
      // 再呼叫 getMakDevicesFromServer 來更新 local cache
      await ctx.dispatch(new RefreshMakDevices()).toPromise();
    } catch (error) {
      console.warn('LoadCacheMakDevices error', error);
      // 如果沒有 cache, 就去 server 取
      await ctx.dispatch(new RefreshMakDevices()).toPromise();
    }
  }

  @Action(LoadCacheMakDevices)
  async loadCacheMakDevices(ctx: StateContext<MakDeviceStateModel>) {
    console.log('[Action] LoadCacheMakDevices');

    const _makDevices: any = await this.storage.get(
      AppSettings.CACHE_KEY_MAK_DEVICE_LIST
    );

    if (_makDevices) {
      ctx.patchState({
        loading: false,
        makDevices: _makDevices,
        hasCache: true,
      });
    } else {
      throw new Error('no cache');
    }
  }

  @Action(RefreshMakDevices)
  async refreshMakDevices(ctx: StateContext<MakDeviceStateModel>) {
    console.log('[Action] RefreshMakDevices');
    await this.getMakDevicesFromServer(ctx);
  }

  @Action(CheckMakStatus)
  async checkMakStatus(
    ctx: StateContext<MakDeviceStateModel>,
    action: CheckMakStatus
  ) {
    console.log('[Action] CheckMakStatus', action.mac_address);

    try {
      ctx.patchState({
        loading: true,
        checkStatusResult: null,
      });

      const result: any = await this.makService.checkMakStatus(
        action.mac_address
      );
      console.log('checkMakStatus result', result);

      ctx.patchState({
        loading: false,
        checkStatusResult: result.data,
      });

      return result;
    } catch (error) {
      console.error('checkMakStatus error', error);
      ctx.patchState({
        loading: false,
        checkStatusResult: null,
      });
      throw error;
    }
  }

  @Action(BindMak)
  async bindMak(ctx: StateContext<MakDeviceStateModel>, action: BindMak) {
    console.log('[Action] BindMak', action);

    try {
      ctx.patchState({
        loading: true,
        bindResult: null,
      });

      const result: any = await this.makService.bindMak(
        action.team_id,
        action.user_id,
        action.mac_address
      );
      console.log('bindMak result', result);

      ctx.patchState({
        loading: false,
        bindResult: {
          success: result.returnCode === 'OK',
          message: result.message,
        },
      });

      // 綁定成功後，重新整理裝置清單
      if (result.returnCode === 'OK') {
        await ctx.dispatch(new RefreshMakDevices()).toPromise();
      }

      return result;
    } catch (error) {
      console.error('bindMak error', error);
      ctx.patchState({
        loading: false,
        bindResult: {
          success: false,
          message: error.message || '綁定失敗',
        },
      });
      throw error;
    }
  }

  @Action(GetMakDevices)
  async getMakDevices(ctx: StateContext<MakDeviceStateModel>) {
    console.log('[Action] GetMakDevices');
    await this.getMakDevicesFromServer(ctx);
  }

  @Action(GetMakMp3s)
  async getMakMp3s(
    ctx: StateContext<MakDeviceStateModel>,
    action: GetMakMp3s
  ) {
    console.log('[Action] GetMakMp3s', action.mak_device_id);

    try {
      ctx.patchState({
        loading: true,
      });

      const result: any = await this.makService.getMakMp3s(
        action.mak_device_id
      );
      console.log('getMakMp3s result', result);

      const state = ctx.getState();
      const newMakMp3Map = { ...state.makMp3Map };
      newMakMp3Map[action.mak_device_id] = result.data;

      ctx.patchState({
        loading: false,
        makMp3Map: newMakMp3Map,
      });

      // 儲存至 local cache
      await this.storage.set(
        `${AppSettings.CACHE_KEY_MAK_MP3_LIST}_${action.mak_device_id}`,
        result.data
      );

      return result;
    } catch (error) {
      console.error('getMakMp3s error', error);
      ctx.patchState({
        loading: false,
      });
      throw error;
    }
  }

  @Action(ProcessMakDevice)
  async processMakDevice(
    ctx: StateContext<MakDeviceStateModel>,
    action: ProcessMakDevice
  ) {
    console.log('processMakDevice', action.mac_address);

    // 儲存 MAC 位址供後續使用
    ctx.patchState({
      lastProcessedMacAddress: action.mac_address,
    });

    // // 檢查 MAC 位址綁定狀態 (移到 AppComponent 處理,因為這邊尚未登入)
    // try {
    //   await ctx.dispatch(new CheckMakStatus(action.mac_address)).toPromise();
    // } catch (error) {
    //   console.error('processMakDevice error', error);
    //   this.util.showToast('處理 MAK 裝置失敗，請稍後再試');
    // }
  }

  private async getMakDevicesFromServer(ctx: StateContext<MakDeviceStateModel>) {
    console.log('getMakDevicesFromServer');
    try {
      ctx.patchState({
        loading: true,
      });

      const result: any = await this.makService.getMakDevices();
      console.log('getMakDevices result', result);

      ctx.patchState({
        loading: false,
        makDevices: result.data,
        hasCache: true,
      });

      // 儲存至 local cache
      await this.storage.set(
        AppSettings.CACHE_KEY_MAK_DEVICE_LIST,
        result.data
      );
    } catch (error) {
      console.error('getMakDevices error', error);
      ctx.patchState({
        loading: false,
      });
    }
  }
}
